import React, { useState, useEffect } from 'react';
import { RadioCard } from '../RadioCard';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { colors } from '../../theme/colors';
import { shadows } from '../../theme/shadows';
import { fetchServicePackages } from '../../lib/api/dataClient';

// --- Local ServiceCard Component ---
interface ServiceCardProps {
    id: string;
    title: string;
    description: string;
    price: string;
    features: string[];
    selected: boolean;
    onSelect: () => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
    id,
    title,
    description,
    price,
    features,
    selected,
    onSelect,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // Container with fixed constraints to prevent overflow
    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
        outline: 'none',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        backgroundColor: selected ? '#FFFBF5' : '#FFFFFF',
        border: selected
            ? `2px solid ${colors.primary.navy}`
            : `1px solid ${isHovered ? colors.primary.navy : '#E5E7EB'}`,
        borderLeft: selected ? `4px solid #C9B37E` : undefined,
        boxShadow: selected
            ? '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)'
            : isHovered
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)'
                : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        padding: spacing[24],
        marginBottom: spacing[16],
        position: 'relative',
        overflow: 'hidden',
        minHeight: 0,
    };

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
    };

    const selectionIndicatorStyle: React.CSSProperties = {
        flexShrink: 0,
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        border: `1px solid ${selected ? colors.primary.navy : '#D1D5DB'}`,
        marginRight: spacing[16],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: selected ? colors.primary.navy : 'transparent',
        transition: 'all 0.2s ease',
        color: '#FFFFFF',
    };

    const contentStyle: React.CSSProperties = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    };

    const topRowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: spacing[4],
    };

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '18px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
        lineHeight: 1.4,
    };

    const priceStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '18px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
        marginLeft: spacing[16],
    };

    const accordionTriggerStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        outline: 'none',
        padding: `${spacing[8]} 0`,
        cursor: 'pointer',
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.text.muted,
        display: 'flex',
        alignItems: 'center',
        gap: spacing[8],
        transition: 'color 0.2s ease',
    };

    const accordionContentStyle: React.CSSProperties = {
        marginTop: spacing[16],
        paddingTop: spacing[16],
        borderTop: '1px solid #E5E7EB',
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        fontWeight: typography.weights.regular,
        color: colors.text.primary,
        lineHeight: 1.7,
    };

    const focusRingStyle: React.CSSProperties = isFocused ? {
        boxShadow: `0 0 0 3px rgba(201, 179, 126, 0.4)`, // Brass accent ring
    } : {};

    return (
        <div
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={() => onSelect()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={{ ...containerStyle, ...focusRingStyle }}
        >
            <div style={headerStyle}>
                <div style={selectionIndicatorStyle}>
                    {selected && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    )}
                </div>
                <div style={contentStyle}>
                    <div style={topRowStyle}>
                        <span style={titleStyle}>{title}</span>
                        <span style={priceStyle}>{price}</span>
                    </div>

                    {/* Only show toggle if there are bullets */}
                    {features.length > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            style={{
                                ...accordionTriggerStyle,
                                color: isHovered || isExpanded ? colors.primary.navy : colors.text.muted,
                            }}
                            type="button"
                        >
                            {isExpanded ? 'Piilota sisältö' : 'Mitä huolto sisältää?'}
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>
                    )}

                    {/* Expanded content - only show bullets, not description */}
                    {isExpanded && features.length > 0 && (
                        <div style={accordionContentStyle}>
                            <ul style={{
                                margin: 0,
                                paddingLeft: spacing[24],
                                listStyleType: 'disc',
                            }}>
                                {features.map((feature, idx) => (
                                    <li key={idx} style={{
                                        marginBottom: spacing[8],
                                        lineHeight: 1.6,
                                    }}>{feature}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---

interface Step2Props {
    engineBrandId?: number | null;
    engineModelId?: number | null;
    engineYear?: number | null;
    selectedPackageId?: string;
    onSelect: (pkgId: string, details?: { title: string; price: string }) => void;
}

export const Step2: React.FC<Step2Props> = ({
    engineBrandId,
    engineModelId,
    engineYear,
    selectedPackageId,
    onSelect
}) => {
    // Local state for fetched packages
    const [servicePackages, setServicePackages] = useState<any[]>([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [packagesError, setPackagesError] = useState<string | null>(null);
    const [isFallback, setIsFallback] = useState(false);
    const [showBlocker, setShowBlocker] = useState(false);

    const handlePackageSelect = (pkgId: string, pkg?: any) => {
        if (pkgId === 'other') {
            onSelect('other', { title: 'Muu / En osaa sanoa', price: '-' });
        } else if (pkg) {
            onSelect(pkgId, { title: pkg.title, price: pkg.price });
        } else {
            const found = servicePackages.find(p => p.id.toString() === pkgId);
            if (found) {
                onSelect(pkgId, { title: found.title, price: found.price });
            } else {
                onSelect(pkgId);
            }
        }
    };

    useEffect(() => {
        // Validate both IDs are valid positive numbers
        const validBrandId = engineBrandId && Number.isFinite(engineBrandId) && engineBrandId > 0;
        const validModelId = engineModelId && Number.isFinite(engineModelId) && engineModelId > 0;

        if (!validBrandId || !validModelId) {
            console.log('[Step2] Missing engine IDs - showing blocker', { engineBrandId, engineModelId });
            setShowBlocker(true);
            setPackagesLoading(false);
            setPackagesError(null);
            setServicePackages([]);
            return;
        }

        // Valid IDs - hide blocker and fetch
        setShowBlocker(false);
        console.log('[Step2] Fetching service packages with', { engineBrandId, engineModelId, engineYear });

        const loadServicePackages = async () => {
            setPackagesLoading(true);
            setPackagesError(null);
            setIsFallback(false);

            try {
                const packages = await fetchServicePackages(
                    engineBrandId,
                    engineModelId,
                    engineYear && Number.isFinite(engineYear) && engineYear > 0 ? engineYear : undefined
                );
                console.log('[XANO] service-packages response:', packages);
                setServicePackages(packages);

                if (packages.length === 0) {
                    setIsFallback(true);
                    onSelect('other', { title: 'Muu / En osaa sanoa', price: '-' });
                } else if (!selectedPackageId) {
                    // Auto-select first package if none selected yet
                    // Make sure we use the ID as string
                    const first = packages[0];
                    onSelect(first.id.toString(), { title: first.title, price: first.price });
                    console.log('[Step2] Auto-selected first package:', first.id);
                } else {
                    // If we have a selection, verify it's still valid (unless it's 'other')
                    if (selectedPackageId !== 'other' && !packages.find(p => p.id.toString() === selectedPackageId)) {
                        // Selection no longer valid (e.g. engine changed), re-select first
                        const first = packages[0];
                        onSelect(first.id.toString(), { title: first.title, price: first.price });
                    }
                }
            } catch (error) {
                console.error('Failed to fetch service packages:', error);
                setPackagesError('Huoltopakettien lataaminen epäonnistui.');
                // Fallback to manual selection on error
                setIsFallback(true);
                onSelect('other', { title: 'Muu / En osaa sanoa', price: '-' });
            } finally {
                setPackagesLoading(false);
            }
        };

        loadServicePackages();
    }, [engineBrandId, engineModelId, engineYear]); // Re-fetch if these change

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '28px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
        marginBottom: spacing[12],
        marginTop: 0,
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        color: colors.text.muted,
        marginBottom: spacing[48],
        lineHeight: 1.6,
    };

    const errorStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        color: '#DC2626',
        marginBottom: spacing[16],
        padding: spacing[12],
        backgroundColor: '#FEE2E2',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };

    const retryButtonStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        padding: `${spacing[4]} ${spacing[8]}`,
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.primary.navy,
        cursor: 'pointer',
        textDecoration: 'underline',
        outline: 'none',
    };

    const fallbackMessageStyle: React.CSSProperties = {
        ...helperStyle,
        padding: spacing[24],
        backgroundColor: '#F3F4F6',
        borderRadius: '8px',
        marginBottom: spacing[24],
    };

    return (
        <div>
            <h2 style={titleStyle}>Mikä näistä huolloista sopii sinulle parhaiten?</h2>

            {/* Blocker: Missing engine IDs */}
            {showBlocker && (
                <div style={{
                    ...fallbackMessageStyle,
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #F59E0B',
                }}>
                    <strong>Valitse moottorin merkki ja malli edellisessä vaiheessa.</strong>
                    <br />
                    Huoltopaketteja ei voida ladata ilman moottorin tietoja.
                </div>
            )}

            {!showBlocker && !packagesLoading && !packagesError && !isFallback && (
                <p style={helperStyle}>
                    Valitsimme nämä vaihtoehdot moottoritietojesi perusteella. Hinnat sisältävät kaiken työn ja tarvikkeet.
                </p>
            )}

            {/* Loading state */}
            {!showBlocker && packagesLoading && (
                <p style={helperStyle}>
                    Ladataan huoltopaketteja...
                </p>
            )}

            {/* Error state */}
            {!showBlocker && packagesError && (
                <div style={errorStyle}>
                    <span>{packagesError}</span>
                    <button
                        type="button"
                        style={retryButtonStyle}
                        onClick={() => window.location.reload()}
                    >
                        Yritä uudelleen
                    </button>
                </div>
            )}

            {/* Fallback message when 0 packages */}
            {!showBlocker && isFallback && !packagesError && (
                <p style={fallbackMessageStyle}>
                    Emme löytäneet valmiita huoltopaketteja tälle moottorille.
                    Voit jatkaa varausta ja kertoa toiveesi lisätietojen kohdalla yhteenvedon yhteydessä.
                </p>
            )}

            {/* Service packages or fallback option */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
                {!packagesLoading && servicePackages.map((service) => (
                    <ServiceCard
                        key={service.id}
                        id={service.id.toString()}
                        title={service.title}
                        description={service.description}
                        price={service.price}
                        features={service.features}
                        selected={selectedPackageId === service.id.toString()}
                        onSelect={() => handlePackageSelect(service.id.toString(), service)}
                    />
                ))}

                {/* Always show fallback option */}
                <div style={{ marginTop: spacing[16] }}>
                    <RadioCard
                        title="En löydä sopivaa huoltoa"
                        description="Voit jatkaa varausta ja kirjoittaa toiveesi lisätietoihin yhteenvedon yhteydessä."
                        selected={selectedPackageId === 'other'}
                        onSelect={() => handlePackageSelect('other')}
                    />
                </div>
            </div>
        </div>
    );
};
